import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  CircularProgress,
  TablePagination,
  FormControlLabel,
  Switch,
  Checkbox,
} from "@mui/material";
import {
  PlayArrowOutlined,
  PauseOutlined,
  CalendarTodayOutlined,
  ReplayOutlined,
  EmailOutlined,
  MoreVertOutlined,
  ClearOutlined,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { format, parseISO, addDays, startOfDay } from "date-fns";
import { getScheduleDatePart } from "../../modules/disconnectSchedules/utils/dateUtils";
import { getChipVariant } from "utils/chipStyles";
import { getAccountDisconnectsForQueue, revertAccountDisconnect, revertAccountDisconnectsBulk, rescheduleAccountDisconnect, markDisconnectAsPending, resendDisconnectNotice, markDisconnectsAsPendingBulk, rescheduleAccountDisconnectsBulk, resendDisconnectNoticesBulk, deleteByUuid } from "modules/accountDisconnects/api/actions";
import { post as createDisconnectHold, postBulk as createDisconnectHoldsBulk } from "modules/disconnectHolds/api/actions";
import { setSnackbar } from "modules/snackbar/api/snackbar";
import { useDispatch } from "react-redux";
import { get as getProperties} from "../../modules/property/api/actions";
import { getPropertyGroups } from "modules/property/api/actions";

function formatDateSafe(val, dateFormat = "MM/dd/yyyy") {
  if (!val) return "N/A";
  const part = getScheduleDatePart(val);
  if (!part) return String(val);
  return format(parseISO(part + "T00:00:00"), dateFormat);
}

const DisconnectQueue = (props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [filters, setFilters] = useState({
    status: "",
    property: "",
    group: "",
    highBalance: false,
    longHold: false,
    aged: false,
    manualHold: false,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [showCompleted, setShowCompleted] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [letter14Day, setLetter14Day] = useState(false);
  const [letter10Day, setLetter10Day] = useState(false);
  const [letterFinalNotice, setLetterFinalNotice] = useState(false);
  const [letterDisconnect, setLetterDisconnect] = useState(false);
  const [holdModalOpen, setHoldModalOpen] = useState(false);
  const [revertModalOpen, setRevertModalOpen] = useState(false);
  const [revertMultiDialogOpen, setRevertMultiDialogOpen] = useState(false);
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [selectedDisconnect, setSelectedDisconnect] = useState(null);
  const [bulkRevertDetailsOpen, setBulkRevertDetailsOpen] = useState(false);
  const [bulkRevertResults, setBulkRevertResults] = useState(null);
  const [bulkHoldDialogOpen, setBulkHoldDialogOpen] = useState(false);
  const [bulkRunNowDialogOpen, setBulkRunNowDialogOpen] = useState(false);
  const [bulkRescheduleDialogOpen, setBulkRescheduleDialogOpen] = useState(false);
  const [bulkResendNoticeDialogOpen, setBulkResendNoticeDialogOpen] = useState(false);
  const [bulkActionResultsOpen, setBulkActionResultsOpen] = useState(false);
  const [bulkActionResults, setBulkActionResults] = useState(null);
  const [bulkActionType, setBulkActionType] = useState(null);
  const [disconnectUuidToName, setDisconnectUuidToName] = useState(new Map());
  const [disconnectUuidToId, setDisconnectUuidToId] = useState(new Map());
  const [holdNote, setHoldNote] = useState("");
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState('disconnectDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedDisconnects, setSelectedDisconnects] = useState(new Set());

  // Helper function to build query params consistently
  const buildQueryParams = (customPage = page, customLimit = limit) => {
    const queryParams = {
      page: customPage,
      pageLimit: customLimit,
      showCompleted: showCompleted,
      showInactive: showInactive,
      letter14Day: letter14Day,
      letter10Day: letter10Day,
      letterFinalNotice: letterFinalNotice,
      letterDisconnect: letterDisconnect,
    };
    
    if (filters.status) {
      queryParams.status = filters.status;
    }
    if (filters.property) {
      queryParams.propertyUuid = filters.property;
    }
    if (filters.group) {
      queryParams.groupId = filters.group;
    }
    if (searchQuery) {
      queryParams.searchQuery = searchQuery;
    }
    if (filters.highBalance) {
      queryParams.highBalance = true;
    }
    if (filters.longHold) {
      queryParams.longHold = true;
    }
    if (filters.aged) {
      queryParams.aged = true;
    }
    if (filters.manualHold) {
      queryParams.manualHold = true;
    }
    if (sortBy) {
      queryParams.sortBy = sortBy;
      queryParams.sortOrder = sortOrder;
    }
    
    return queryParams;
  };

  useEffect(() => {
    const queryParams = buildQueryParams();
    props.getAccountDisconnectsForQueue(queryParams, true);
    props.getProperties();
    props.getPropertyGroups({ limit: 100 });
    setSelectedDisconnects(new Set());
  }, [page, limit, filters.status, filters.property, filters.group, filters.highBalance, filters.longHold, filters.aged, filters.manualHold, searchQuery, showCompleted, showInactive, letter14Day, letter10Day, letterFinalNotice, letterDisconnect, sortBy, sortOrder]);

  // Get data from props - using accountDisconnects queue state
  const disconnects = props.accountDisconnects?.disconnectQueue?.queue?.data?.data || [];
  const total = props.accountDisconnects?.disconnectQueue?.queue?.data?.totalRows || 0;
  const properties = props.properties?.data?.propertiesList?.items || [];
  const propertyGroups = props.properties?.propertyGroups?.items || [];
  const isLoading = props.accountDisconnects?.disconnectQueue?.queue?.isLoading || false;

  const getStatusChip = (status, reason) => {
    let chipProps;
    let label = status || "Unknown";
    const normalizedStatus = status?.toLowerCase().replace(/\s+/g, "");

    switch (normalizedStatus) {
      case "scheduled":
        chipProps = {
          variant: "filled",
          sx: {
            backgroundColor: "#e3f2fd",
            color: "#1976d2",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
          },
        };
        break;
      case "auto-held":
      case "held":
        chipProps = getChipVariant("warning", theme);
        break;
      case "needsreset":
      case "needs-reset":
        chipProps = getChipVariant("error", theme);
        break;
      case "inprogress":
      case "in-progress":
        chipProps = getChipVariant("success", theme);
        break;
      case "needsreconnect":
      case "needs-reconnect":
        chipProps = getChipVariant("warning", theme);
        break;
      case "completed":
        chipProps = getChipVariant("success", theme);
        break;
      case "closed":
        chipProps = getChipVariant("default", theme);
        break;
      default:
        chipProps = getChipVariant("default", theme);
    }

    return (
      <Box>
        <Chip
          label={label}
          {...chipProps}
          size="small"
          sx={{ mb: reason ? 0.5 : 0 }}
        />
        {reason && (
          <Typography variant="caption" color="text.secondary" display="block">
            {reason}
          </Typography>
        )}
      </Box>
    );
  };

  const renderFlags = (disconnect) => {
    const flags = [];
    
    if (disconnect.highBalance === 1 || disconnect.highBalance === true) {
      flags.push(
        <Chip
          key="highBalance"
          label="High Balance"
          size="small"
          sx={{
            backgroundColor: "#ff9800",
            color: "#ffffff",
            fontSize: "0.7rem",
            height: "20px",
            fontWeight: 500,
            mr: 0.5,
            mb: 0.5,
          }}
        />
      );
    }
    
    if (disconnect.longHold === 1 || disconnect.longHold === true) {
      flags.push(
        <Chip
          key="longHold"
          label="Long Hold"
          size="small"
          sx={{
            backgroundColor: "#f44336",
            color: "#ffffff",
            fontSize: "0.7rem",
            height: "20px",
            fontWeight: 500,
            mr: 0.5,
            mb: 0.5,
          }}
        />
      );
    }
    
    if (disconnect.aged === 1 || disconnect.aged === true) {
      flags.push(
        <Chip
          key="aged"
          label="Aged"
          size="small"
          sx={{
            backgroundColor: "#9c27b0",
            color: "#ffffff",
            fontSize: "0.7rem",
            height: "20px",
            fontWeight: 500,
            mr: 0.5,
            mb: 0.5,
          }}
        />
      );
    }
    
    if (disconnect.manualHold) {
      const manualHoldDate = formatDateSafe(disconnect.manualHold);
      flags.push(
        <Tooltip 
          key="manualHold" 
          title={`Manual hold flag: ${manualHoldDate}`}
          arrow
        >
          <Chip
            label={`Manual Hold ${manualHoldDate}`}
            size="small"
            icon={<CalendarTodayOutlined sx={{ fontSize: "0.875rem !important", color: "#ffffff !important" }} />}
            sx={{
              backgroundColor: "#2196f3",
              color: "#ffffff",
              fontSize: "0.7rem",
              height: "20px",
              fontWeight: 500,
              mr: 0.5,
              mb: 0.5,
              "& .MuiChip-icon": {
                marginLeft: "4px",
              },
            }}
          />
        </Tooltip>
      );
    }
    
    if (flags.length === 0) {
      return (
        <Typography variant="caption" color="text.secondary">
          None
        </Typography>
      );
    }
    
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {flags}
      </Box>
    );
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
    // Reset page to 1 when any filter changes (including flags) since backend now handles filtering
    setPage(1);
  };

  const handleResetAllFilters = () => {
    // Reset all filters to initial state
    setFilters({
      status: "",
      property: "",
      group: "",
      highBalance: false,
      longHold: false,
      aged: false,
      manualHold: false,
    });
    setSearchQuery("");
    setShowCompleted(false);
    setShowInactive(false);
    setLetter14Day(false);
    setLetter10Day(false);
    setLetterFinalNotice(false);
    setLetterDisconnect(false);
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage + 1); // MUI TablePagination uses 0-based index
  };

  const handleRowsPerPageChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSortRequest = (field) => {
    let newSortBy = field;
    let newSortOrder = 'desc';

    if (sortBy === field) {
      newSortOrder = sortOrder === 'desc' ? 'asc' : 'desc';
    }

    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setPage(1);
  };

  const getRunNowType = (disconnect) => {
    const stage = getCurrentStage(disconnect);
    if (stage === "14-day" || stage === "10-day") {
      return "final_ready";
    }
    return "";
  };

  const getRunNowLabel = (disconnect) => {
    const stage = getCurrentStage(disconnect);
    if (stage === "14-day" || stage === "10-day") {
      return "Generate Final Notice";
    }
    return "Process Disconnect";
  };

  const handleRunNow = async (disconnect) => {
    if (!disconnect) {
      return;
    }

    const disconnectUuid = disconnect.uuid || disconnect.disconnectUuid;
    if (!disconnectUuid) {
      dispatch(setSnackbar(true, "error", "Disconnect UUID not found"));
      return;
    }

    const pendingType = getRunNowType(disconnect);
    const actionLabel = getRunNowLabel(disconnect);

    try {
      await props.markDisconnectAsPending(disconnectUuid, null, false, pendingType);
      dispatch(setSnackbar(true, "success", `${actionLabel} - marked as pending successfully`));
      setActionMenuAnchor(null);
      
      // Refresh the queue data
      const queryParams = buildQueryParams();
      props.getAccountDisconnectsForQueue(queryParams, true);
    } catch (error) {
      console.error("Error marking disconnect as pending:", error);
      dispatch(setSnackbar(true, "error", error.message || "Failed to mark disconnect as pending"));
    }
  };

  const handleHold = (disconnect) => {
    setSelectedDisconnect(disconnect);
    setHoldNote("");
    setHoldModalOpen(true);
  };

  const handleReschedule = (disconnect) => {
    setSelectedDisconnect(disconnect);
    setRescheduleModalOpen(true);
    setActionMenuAnchor(null);
  };

  const handleRescheduleSubmit = async () => {
    if (!selectedDisconnect) {
      return;
    }

    const disconnectUuid = selectedDisconnect.uuid || selectedDisconnect.disconnectUuid;
    if (!disconnectUuid) {
      dispatch(setSnackbar(true, "error", "Disconnect UUID not found"));
      setRescheduleModalOpen(false);
      setSelectedDisconnect(null);
      return;
    }

    try {
      await props.rescheduleAccountDisconnect(disconnectUuid);
      
      dispatch(setSnackbar(true, "success", "Disconnection rescheduled successfully"));
      setRescheduleModalOpen(false);
      setSelectedDisconnect(null);
      
      // Refresh the queue data
      const queryParams = buildQueryParams();
      props.getAccountDisconnectsForQueue(queryParams, true);
    } catch (error) {
      console.error("Error rescheduling disconnect:", error);
      dispatch(setSnackbar(true, "error", error.message || "Failed to reschedule disconnection"));
    }
  };

  const handleRevert = (disconnect) => {
    setSelectedDisconnect(disconnect);
    setRevertModalOpen(true);
    setActionMenuAnchor(null);
  };

  const handleCloseDisconnect = (disconnect) => {
    setSelectedDisconnect(disconnect);
    setCloseModalOpen(true);
    setActionMenuAnchor(null);
  };

  const handleCloseSubmit = async () => {
    if (!selectedDisconnect) return;
    try {
      await props.deleteByUuid(selectedDisconnect.uuid, false);
      dispatch(setSnackbar(true, "success", "Disconnect closed successfully"));
      setCloseModalOpen(false);
      setSelectedDisconnect(null);
      const queryParams = buildQueryParams();
      props.getAccountDisconnectsForQueue(queryParams, true);
    } catch (error) {
      console.error("Error closing disconnect:", error);
      dispatch(setSnackbar(true, "error", error.message || "Failed to close disconnect"));
    }
  };

  const handleRevertMulti = () => {
    if (selectedDisconnects.size === 0) {
      return;
    }
    setRevertMultiDialogOpen(true);
  };

  const handleHoldMulti = () => {
    if (selectedDisconnects.size === 0) {
      return;
    }
    setHoldNote("");
    setBulkHoldDialogOpen(true);
  };

  const handleRunNowMulti = () => {
    if (selectedDisconnects.size === 0) {
      return;
    }
    setBulkRunNowDialogOpen(true);
  };

  const handleRescheduleMulti = () => {
    if (selectedDisconnects.size === 0) {
      return;
    }
    setBulkRescheduleDialogOpen(true);
  };

  const handleResendNoticeMulti = () => {
    if (selectedDisconnects.size === 0) {
      return;
    }
    setBulkResendNoticeDialogOpen(true);
  };

  const handleSelectDisconnect = (disconnectUuid) => {
    setSelectedDisconnects((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(disconnectUuid)) {
        newSet.delete(disconnectUuid);
      } else {
        newSet.add(disconnectUuid);
      }
      return newSet;
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allUuids = new Set(disconnects.map((d) => d.uuid || d.disconnectUuid).filter(Boolean));
      setSelectedDisconnects(allUuids);
    } else {
      setSelectedDisconnects(new Set());
    }
  };

  const isAllSelected = disconnects.length > 0 && selectedDisconnects.size === disconnects.length;
  const isIndeterminate = selectedDisconnects.size > 0 && selectedDisconnects.size < disconnects.length;

  const getCurrentStage = (disconnect) => {
    // Determine current stage
    if (disconnect.letterDisconnectId) {
      return "Disconnected";
    } else if (disconnect.letterFinalNoticeId) {
      return "Final Notice";
    } else if (disconnect.letter10DayId) {
      return "10-day";
    } else if (disconnect.letter14DayId) {
      return "14-day";
    }
    return null;
  };

  const getPreviousStage = (disconnect) => {
    // Determine current stage and return target stage to revert to
    // Backend logic (updated to handle 10-day revert):
    // - newStatus="14-day" → deletes letter10DayId (if at 10-day) OR letterFinalNoticeId (if at Final)
    // - newStatus="10-day" → deletes letterFinalNoticeId (if at Final)
    // - newStatus="Final" → deletes letterDisconnectId
    //
    // Flow: 14-day → 10-day → Final → Disconnect
    // To revert, we delete the current stage's letter
    
    // Check in order of most recent to least recent
    if (disconnect.letterDisconnectId) {
      // Current: Disconnected → Target: Final
      // Backend: newStatus="Final" deletes letterDisconnectId
      return "Final";
    } else if (disconnect.letterFinalNoticeId) {
      // Current: Final → Target: 10-day or 14-day (check which letter exists)
      // Check 10-day first since customers can get both letters, and 10-day is more recent
      // Backend: newStatus="10-day" or "14-day" deletes letterFinalNoticeId
      if (disconnect.letter10DayId) {
        return "10-day";
      } else if (disconnect.letter14DayId) {
        return "14-day";
      }
      return null;
    } else if (disconnect.letter10DayId) {
      // Current: 10-day → Target: 14-day
      // Backend: newStatus="14-day" deletes letter10DayId (now supported!)
      return "14-day";
    } else if (disconnect.letter14DayId) {
      // Current: 14-day → Cannot revert (earliest stage)
      return null;
    }
    return null;
  };

  const handleRevertSubmit = async () => {
    if (!selectedDisconnect) {
      return;
    }

    const disconnectUuid = selectedDisconnect.uuid || selectedDisconnect.disconnectUuid;
    if (!disconnectUuid) {
      dispatch(setSnackbar(true, "error", "Disconnect UUID not found"));
      setRevertModalOpen(false);
      setSelectedDisconnect(null);
      return;
    }

    // Determine previous stage
    const previousStage = getPreviousStage(selectedDisconnect);
    if (!previousStage) {
      const currentStage = getCurrentStage(selectedDisconnect);
      if (currentStage === "14-day") {
        dispatch(setSnackbar(true, "error", "Cannot revert - already at the earliest stage (14-day)"));
      } else {
        dispatch(setSnackbar(true, "error", `Cannot revert from ${currentStage || "unknown stage"}`));
      }
      setRevertModalOpen(false);
      setSelectedDisconnect(null);
      return;
    }

    try {
      await props.revertAccountDisconnect(disconnectUuid, previousStage);
      dispatch(setSnackbar(true, "success", "Disconnect reverted to previous stage successfully"));
      setRevertModalOpen(false);
      setSelectedDisconnect(null);
      const queryParams = buildQueryParams();
      props.getAccountDisconnectsForQueue(queryParams, true);
    } catch (error) {
      console.error("Error reverting disconnect:", error);
      dispatch(setSnackbar(true, "error", error.message || "Failed to revert disconnect"));
    }
  };

  const handleResendLetter = async (disconnect) => {
    if (!disconnect) {
      return;
    }

    const disconnectUuid = disconnect.uuid || disconnect.disconnectUuid;
    if (!disconnectUuid) {
      dispatch(setSnackbar(true, "error", "Disconnect UUID not found"));
      return;
    }

    try {
      await props.resendDisconnectNotice(disconnectUuid);
      dispatch(setSnackbar(true, "success", "Notice resent successfully"));
      setActionMenuAnchor(null);
      
      // Refresh the queue data
      const queryParams = buildQueryParams();
      props.getAccountDisconnectsForQueue(queryParams, true);
    } catch (error) {
      console.error("Error resending notice:", error);
      dispatch(setSnackbar(true, "error", error.message || "Failed to resend notice"));
    }
  };

  const handleHoldSubmit = async () => {
    if (!holdNote.trim()) {
      return;
    }
    
    if (!selectedDisconnect) {
      return;
    }

    try {
      const accountId = selectedDisconnect.accountId || selectedDisconnect.id;
      if (!accountId) {
        dispatch(setSnackbar(true, "error", "Account ID not found"));
        return;
      }

      // Format expiresAt as YYYY-MM-DD HH:mm:ss (normalized to midnight)
      const expiresAtDate = startOfDay(addDays(new Date(), 30));
      const expiresAt = format(expiresAtDate, "yyyy-MM-dd HH:mm:ss");

      // Use the same format as the create hold modal
      const holdData = {
        holdType: 'ACCOUNT',
        accountId: accountId,
        reason: holdNote.trim(),
        notes: holdNote.trim(),
        expiresAt: expiresAt,
        isActive: 1,
        liftReason: '',
      };

      await props.createDisconnectHold(holdData);
      
      dispatch(setSnackbar(true, "success", "Disconnection held successfully"));
      setHoldModalOpen(false);
      setSelectedDisconnect(null);
      setHoldNote("");
      
      // Refresh the queue data
      const queryParams = buildQueryParams();
      props.getAccountDisconnectsForQueue(queryParams, true);
    } catch (error) {
      console.error("Error holding disconnect:", error);
      dispatch(setSnackbar(true, "error", error.message || "Failed to hold disconnection"));
    }
  };

  const statusOptions = [
    { value: "", label: "All" },
    { value: "Needs Reconnect", label: "Needs Reconnect" },
    { value: "Scheduled", label: "Scheduled" },
    { value: "Auto-Held", label: "Auto-Held" },
    { value: "Needs Reset", label: "Needs Reset" },
    { value: "In Progress", label: "In Progress" },
    { value: "Closed", label: "Closed" },
  ];

  const getStatusFilterStyle = (statusValue) => {
    if (filters.status === statusValue) {
      return {
        backgroundColor: "#1976d2",
        color: "#ffffff !important",
        border: "1px solid #1976d2",
        fontWeight: 500,
        borderRadius: "16px",
        fontSize: "0.875rem",
        textTransform: "none",
        "&:hover": {
          backgroundColor: "#1565c0",
          border: "1px solid #1565c0",
        }
      };
    }
    return {
      backgroundColor: "rgba(158, 158, 158, 0.1)",
      color: "#757575 !important",
      border: "1px solid #bdbdbd",
      fontWeight: 500,
      borderRadius: "16px",
      fontSize: "0.875rem",
      textTransform: "none",
      "&:hover": {
        backgroundColor: "rgba(158, 158, 158, 0.2)",
        border: "1px solid #bdbdbd",
      }
    };
  };

  const getLetterFilterStyle = (isSelected) => {
    if (isSelected) {
      return {
        backgroundColor: "#1976d2",
        color: "#ffffff !important",
        border: "1px solid #1976d2",
        fontWeight: 500,
        borderRadius: "16px",
        fontSize: "0.875rem",
        textTransform: "none",
        "&:hover": {
          backgroundColor: "#1565c0",
          border: "1px solid #1565c0",
        }
      };
    }
    return {
      backgroundColor: "rgba(158, 158, 158, 0.1)",
      color: "#757575 !important",
      border: "1px solid #bdbdbd",
      fontWeight: 500,
      borderRadius: "16px",
      fontSize: "0.875rem",
      textTransform: "none",
      "&:hover": {
        backgroundColor: "rgba(158, 158, 158, 0.2)",
        border: "1px solid #bdbdbd",
      }
    };
  };

  const getFlagFilterStyle = (isSelected, color) => {
    if (isSelected) {
      return {
        backgroundColor: color,
        color: "#ffffff !important",
        border: `1px solid ${color}`,
        fontWeight: 500,
        borderRadius: "16px",
        fontSize: "0.875rem",
        textTransform: "none",
        "&:hover": {
          backgroundColor: color,
          opacity: 0.9,
        }
      };
    }
    return {
      backgroundColor: "rgba(158, 158, 158, 0.1)",
      color: "#757575 !important",
      border: "1px solid #bdbdbd",
      fontWeight: 500,
      borderRadius: "16px",
      fontSize: "0.875rem",
      textTransform: "none",
      "&:hover": {
        backgroundColor: "rgba(158, 158, 158, 0.2)",
        border: "1px solid #bdbdbd",
      }
    };
  };

  const fullAddressField = (disconnect) =>
    [
      disconnect.address1,
      disconnect.address2,
      disconnect.city,
      disconnect.state,
      disconnect.zip
    ].filter(Boolean)
    .join(", ")
    .trim() || "N/A";

  const fullName = (disconnect) =>
    [
      disconnect.firstName,
      disconnect.lastName
    ].filter(Boolean)
    .join(" ")
    .trim() || "N/A";

  const formatCurrency = (amount) => {
    if (amount == null || amount === undefined) return "N/A";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatLetterDates = (disconnect) => {
    const letters = [];
    if (disconnect.letter14DaySendDate) {
      letters.push(`14-Day: ${formatDateSafe(disconnect.letter14DaySendDate)}`);
    }
    if (disconnect.letter10DaySendDate) {
      letters.push(`10-Day: ${formatDateSafe(disconnect.letter10DaySendDate)}`);
    }
    if (disconnect.letterFinalNoticeSendDate) {
      letters.push(`Final: ${formatDateSafe(disconnect.letterFinalNoticeSendDate)}`);
    }
    if (disconnect.letterDisconnectSendDate) {
      letters.push(`Disconnect: ${formatDateSafe(disconnect.letterDisconnectSendDate)}`);
    }
    return letters.length > 0 ? letters.join(", ") : "N/A";
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          Disconnection Queue
        </Typography>
        <Chip
          label={`Queue Items (${total})`}
          color="primary"
          sx={{ fontWeight: "bold" }}
        />
      </Box>

      {/* Floating Action Buttons */}
      {selectedDisconnects.size > 0 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
            flexWrap: 'wrap',
            maxWidth: 'calc(100vw - 48px)',
          }}
        >
          {/* Clear Selection Button */}
          <IconButton
            onClick={() => setSelectedDisconnects(new Set())}
            sx={{
              width: 48,
              height: 48,
              backgroundColor: 'grey.600',
              color: 'white',
              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)',
              '&:hover': {
                backgroundColor: 'grey.700',
                boxShadow: '0 12px 24px rgba(0, 0, 0, 0.25), 0 6px 12px rgba(0, 0, 0, 0.2)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <ClearOutlined />
          </IconButton>

          {/* Hold Selected Button */}
          <Button
            variant="contained"
            color="warning"
            size="medium"
            startIcon={<PauseOutlined />}
            onClick={handleHoldMulti}
            sx={{
              minWidth: 160,
              color: 'white',
              height: 48,
              borderRadius: 2,
              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)',
              fontSize: '0.9rem',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                boxShadow: '0 12px 24px rgba(0, 0, 0, 0.25), 0 6px 12px rgba(0, 0, 0, 0.2)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Hold ({selectedDisconnects.size})
          </Button>

          {/* Run Now Selected Button */}
          <Button
            variant="contained"
            color="success"
            size="medium"
            startIcon={<PlayArrowOutlined />}
            onClick={handleRunNowMulti}
            sx={{
              minWidth: 160,
              height: 48,
              borderRadius: 2,
              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)',
              fontSize: '0.9rem',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                boxShadow: '0 12px 24px rgba(0, 0, 0, 0.25), 0 6px 12px rgba(0, 0, 0, 0.2)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Run Now ({selectedDisconnects.size})
          </Button>

          {/* Reschedule Selected Button */}
          <Button
            variant="contained"
            color="info"
            size="medium"
            startIcon={<CalendarTodayOutlined />}
            onClick={handleRescheduleMulti}
            sx={{
              minWidth: 160,
              height: 48,
              borderRadius: 2,
              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)',
              fontSize: '0.9rem',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                boxShadow: '0 12px 24px rgba(0, 0, 0, 0.25), 0 6px 12px rgba(0, 0, 0, 0.2)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Reschedule ({selectedDisconnects.size})
          </Button>

          {/* Resend Notice Selected Button */}
          <Button
            variant="contained"
            color="primary"
            size="medium"
            startIcon={<EmailOutlined />}
            onClick={handleResendNoticeMulti}
            sx={{
              minWidth: 160,
              height: 48,
              borderRadius: 2,
              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)',
              fontSize: '0.9rem',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                boxShadow: '0 12px 24px rgba(0, 0, 0, 0.25), 0 6px 12px rgba(0, 0, 0, 0.2)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Resend Notice ({selectedDisconnects.size})
          </Button>

          {/* Revert Selected Button */}
          <Button
            variant="contained"
            color="error"
            size="medium"
            startIcon={<ReplayOutlined />}
            onClick={handleRevertMulti}
            sx={{
              minWidth: 160,
              height: 48,
              borderRadius: 2,
              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)',
              fontSize: '0.9rem',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                boxShadow: '0 12px 24px rgba(0, 0, 0, 0.25), 0 6px 12px rgba(0, 0, 0, 0.2)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Revert ({selectedDisconnects.size})
          </Button>
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
        {/* Top row: Search and dropdowns */}
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            size="small"
            label="Search"
            placeholder="Account #, Customer Name..."
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Property</InputLabel>
            <Select
              value={filters.property}
              label="Property"
              onChange={(e) => handleFilterChange("property", e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {properties.map((property) => (
                <MenuItem key={property.uuid} value={property.uuid}>
                  {property.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Group</InputLabel>
            <Select
              value={filters.group}
              label="Group"
              onChange={(e) => handleFilterChange("group", e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {propertyGroups.map((group) => (
                <MenuItem key={group.uuid || group.id} value={group.id?.toString() || group.uuid}>
                  {group.name || group.groupName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={showCompleted}
                onChange={(e) => setShowCompleted(e.target.checked)}
                size="small"
              />
            }
            label="Completed"
            sx={{ ml: 1 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                size="small"
              />
            }
            label="Inactive"
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearOutlined />}
            onClick={handleResetAllFilters}
            sx={{ ml: 'auto' }}
          >
            Reset All Filters
          </Button>
        </Box>

        {/* Filter buttons - column layout */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' }, gap: 2 }}>
          {/* Status Filters Column */}
          <Box>
            <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500, color: '#666' }}>
              Status
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              <Button
                onClick={() => handleFilterChange("status", "")}
                sx={getStatusFilterStyle("")}
                variant="outlined"
                size="small"
              >
                All
              </Button>
              {statusOptions.filter(opt => opt.value).map((status) => (
                <Button
                  key={status.value}
                  onClick={() => handleFilterChange("status", status.value)}
                  sx={getStatusFilterStyle(status.value)}
                  variant="outlined"
                  size="small"
                >
                  {status.label}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Letter Notice Filters Column */}
          <Box>
            <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500, color: '#666' }}>
              Letter
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              <Button
                onClick={() => {
                  setLetter14Day(!letter14Day);
                  setPage(1);
                }}
                sx={getLetterFilterStyle(letter14Day)}
                variant="outlined"
                size="small"
              >
                14-Day
              </Button>
              <Button
                onClick={() => {
                  setLetter10Day(!letter10Day);
                  setPage(1);
                }}
                sx={getLetterFilterStyle(letter10Day)}
                variant="outlined"
                size="small"
              >
                10-Day
              </Button>
              <Button
                onClick={() => {
                  setLetterFinalNotice(!letterFinalNotice);
                  setPage(1);
                }}
                sx={getLetterFilterStyle(letterFinalNotice)}
                variant="outlined"
                size="small"
              >
                Final
              </Button>
              <Button
                onClick={() => {
                  setLetterDisconnect(!letterDisconnect);
                  setPage(1);
                }}
                sx={getLetterFilterStyle(letterDisconnect)}
                variant="outlined"
                size="small"
              >
                Disconnect
              </Button>
            </Box>
          </Box>

          {/* Flag Filters Column */}
          <Box>
            <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500, color: '#666' }}>
              Flags
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              <Button
                onClick={() => handleFilterChange("highBalance", !filters.highBalance)}
                sx={getFlagFilterStyle(filters.highBalance, "#ff9800")}
                variant="outlined"
                size="small"
              >
                High Balance
              </Button>
              <Button
                onClick={() => handleFilterChange("longHold", !filters.longHold)}
                sx={getFlagFilterStyle(filters.longHold, "#f44336")}
                variant="outlined"
                size="small"
              >
                Long Hold
              </Button>
              <Button
                onClick={() => handleFilterChange("aged", !filters.aged)}
                sx={getFlagFilterStyle(filters.aged, "#9c27b0")}
                variant="outlined"
                size="small"
              >
                Aged
              </Button>
              <Button
                onClick={() => handleFilterChange("manualHold", !filters.manualHold)}
                sx={getFlagFilterStyle(filters.manualHold, "#2196f3")}
                variant="outlined"
                size="small"
              >
                Manual Hold
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer 
        component={Paper}
        sx={{
          overflowX: 'auto',
          overflowY: 'auto',
          position: 'relative',
          maxHeight: 'calc(100vh - 300px)',
        }}
      >
        {disconnects.length === 0 && !isLoading ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography color="text.secondary">
              No disconnects found in queue
            </Typography>
          </Box>
        ) : (
          <Box sx={{ position: 'relative' }}>
            {isLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 10,
                  backdropFilter: 'blur(2px)',
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <Table stickyHeader sx={{ opacity: isLoading ? 0.5 : 1 }}>
            <TableHead
              sx={{
                '& .MuiTableCell-head': {
                  backgroundColor: '#f5f5f5',
                  borderBottom: 'none',
                },
              }}
            >
              <TableRow>
                <TableCell
                  padding="checkbox"
                  sx={{
                    position: 'sticky',
                    left: 0,
                    top: 0,
                    zIndex: 20,
                    backgroundColor: '#f5f5f5',
                    borderRight: 'none',
                    borderBottom: 'none',
                    width: 48,
                  }}
                >
                  <Checkbox
                    indeterminate={isIndeterminate}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    color="primary"
                  />
                </TableCell>
                <TableCell
                  sx={{
                    position: 'sticky',
                    left: 48,
                    top: 0,
                    zIndex: 19,
                    backgroundColor: '#f5f5f5',
                    borderRight: 'none',
                    borderBottom: 'none',
                    minWidth: 150,
                    width: 150,
                  }}
                >
                  Disconnect ID
                </TableCell>
                <TableCell
                  sx={{
                    position: 'sticky',
                    left: 198,
                    top: 0,
                    zIndex: 19,
                    backgroundColor: '#f5f5f5',
                    borderRight: '2px solid',
                    borderRightColor: 'divider',
                    borderBottom: 'none',
                    minWidth: 120,
                    width: 120,
                  }}
                >
                  Account #
                </TableCell>
                <TableCell>Account Holder</TableCell>
                <TableCell>Account Status</TableCell>
                <TableCell>Account Type</TableCell>
                <TableCell>Property</TableCell>
                <TableCell>Group</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Meter Info</TableCell>
                <TableCell>Letter Sent Dates (Type)</TableCell>
                <TableCell
                  sortDirection={sortBy === 'disconnectDate' ? sortOrder : false}
                >
                  <TableSortLabel
                    active={sortBy === 'disconnectDate'}
                    direction={sortBy === 'disconnectDate' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('disconnectDate')}
                    sx={{
                      '& .MuiTableSortLabel-icon': {
                        opacity: sortBy === 'disconnectDate' ? 1 : 0.5,
                        fontSize: '1.2rem',
                        color: sortBy === 'disconnectDate' ? 'primary.main' : 'text.secondary',
                      },
                      '&:hover .MuiTableSortLabel-icon': {
                        opacity: 1,
                        color: 'primary.main',
                      },
                      '&.Mui-active': {
                        color: 'primary.main',
                        fontWeight: 600,
                      },
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    Disconnect Date
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={sortBy === 'lastPaymentDate' ? sortOrder : false}
                >
                  <TableSortLabel
                    active={sortBy === 'lastPaymentDate'}
                    direction={sortBy === 'lastPaymentDate' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('lastPaymentDate')}
                    sx={{
                      '& .MuiTableSortLabel-icon': {
                        opacity: sortBy === 'lastPaymentDate' ? 1 : 0.5,
                        fontSize: '1.2rem',
                        color: sortBy === 'lastPaymentDate' ? 'primary.main' : 'text.secondary',
                      },
                      '&:hover .MuiTableSortLabel-icon': {
                        opacity: 1,
                        color: 'primary.main',
                      },
                      '&.Mui-active': {
                        color: 'primary.main',
                        fontWeight: 600,
                      },
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    Last Paid
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={sortBy === 'disconnectAmt' ? sortOrder : false}
                >
                  <TableSortLabel
                    active={sortBy === 'disconnectAmt'}
                    direction={sortBy === 'disconnectAmt' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('disconnectAmt')}
                    sx={{
                      '& .MuiTableSortLabel-icon': {
                        opacity: sortBy === 'disconnectAmt' ? 1 : 0.5,
                        fontSize: '1.2rem',
                        color: sortBy === 'disconnectAmt' ? 'primary.main' : 'text.secondary',
                      },
                      '&:hover .MuiTableSortLabel-icon': {
                        opacity: 1,
                        color: 'primary.main',
                      },
                      '&.Mui-active': {
                        color: 'primary.main',
                        fontWeight: 600,
                      },
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    Disconnect Amt
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={sortBy === 'accountBalance' ? sortOrder : false}
                >
                  <TableSortLabel
                    active={sortBy === 'accountBalance'}
                    direction={sortBy === 'accountBalance' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('accountBalance')}
                    sx={{
                      '& .MuiTableSortLabel-icon': {
                        opacity: sortBy === 'accountBalance' ? 1 : 0.5,
                        fontSize: '1.2rem',
                        color: sortBy === 'accountBalance' ? 'primary.main' : 'text.secondary',
                      },
                      '&:hover .MuiTableSortLabel-icon': {
                        opacity: 1,
                        color: 'primary.main',
                      },
                      '&.Mui-active': {
                        color: 'primary.main',
                        fontWeight: 600,
                      },
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    Account Balance
                  </TableSortLabel>
                </TableCell>
                <TableCell>Queue Status</TableCell>
                <TableCell>Flags</TableCell>
                <TableCell
                  sortDirection={sortBy === 'daysInQueue' ? sortOrder : false}
                >
                  <TableSortLabel
                    active={sortBy === 'daysInQueue'}
                    direction={sortBy === 'daysInQueue' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('daysInQueue')}
                    sx={{
                      '& .MuiTableSortLabel-icon': {
                        opacity: sortBy === 'daysInQueue' ? 1 : 0.5,
                        fontSize: '1.2rem',
                        color: sortBy === 'daysInQueue' ? 'primary.main' : 'text.secondary',
                      },
                      '&:hover .MuiTableSortLabel-icon': {
                        opacity: 1,
                        color: 'primary.main',
                      },
                      '&.Mui-active': {
                        color: 'primary.main',
                        fontWeight: 600,
                      },
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    Days in Queue
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={sortBy === 'created' ? sortOrder : false}
                >
                  <TableSortLabel
                    active={sortBy === 'created'}
                    direction={sortBy === 'created' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('created')}
                    sx={{
                      '& .MuiTableSortLabel-icon': {
                        opacity: sortBy === 'created' ? 1 : 0.5,
                        fontSize: '1.2rem',
                        color: sortBy === 'created' ? 'primary.main' : 'text.secondary',
                      },
                      '&:hover .MuiTableSortLabel-icon': {
                        opacity: 1,
                        color: 'primary.main',
                      },
                      '&.Mui-active': {
                        color: 'primary.main',
                        fontWeight: 600,
                      },
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {disconnects.map((disconnect) => {
                const disconnectId = disconnect.id || "N/A";
                const disconnectUuid = disconnect.uuid;
                const normalizedStatus = disconnect.queueStatus || "Unknown";
                const isSelected = selectedDisconnects.has(disconnectUuid);

                const selectedBg = '#e3f2fd';
                const hoverBg = '#f5f5f5';
                const selectedHoverBg = '#d6ebfa';
                const stickyBg = isSelected ? selectedBg : '#fff';

                return (
                  <TableRow 
                    key={disconnectUuid || disconnect.id} 
                    hover
                    selected={isSelected}
                    sx={{
                      '&.Mui-selected': {
                        backgroundColor: selectedBg,
                      },
                      '&.Mui-selected:hover': {
                        backgroundColor: selectedHoverBg,
                      },
                      '&:hover': {
                        '& .sticky-cell-0': {
                          backgroundColor: isSelected ? `${selectedHoverBg} !important` : `${hoverBg} !important`,
                        },
                        '& .sticky-cell-1': {
                          backgroundColor: isSelected ? `${selectedHoverBg} !important` : `${hoverBg} !important`,
                        },
                        '& .sticky-cell-2': {
                          backgroundColor: isSelected ? `${selectedHoverBg} !important` : `${hoverBg} !important`,
                        },
                      },
                    }}
                  >
                    {/* Checkbox */}
                    <TableCell
                      padding="checkbox"
                      className="sticky-cell-0"
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 15,
                        backgroundColor: stickyBg,
                        borderRight: 'none',
                        width: 48,
                        boxShadow: '2px 0 4px -2px rgba(0, 0, 0, 0.1)',
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleSelectDisconnect(disconnectUuid)}
                        color="primary"
                      />
                    </TableCell>
                    {/* Disconnect ID */}
                    <TableCell
                      className="sticky-cell-1"
                      sx={{
                        position: 'sticky',
                        left: 48,
                        zIndex: 14,
                        backgroundColor: stickyBg,
                        borderRight: 'none',
                        minWidth: 150,
                        width: 150,
                        boxShadow: '2px 0 4px -2px rgba(0, 0, 0, 0.1)',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: "primary.main",
                          cursor: "pointer",
                          fontWeight: "medium",
                          position: 'relative',
                          zIndex: 1,
                          "&:hover": { textDecoration: "underline" },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (disconnectUuid) {
                            window.open(`/disconnect/${disconnectUuid}`, '_blank');
                          }
                        }}
                      >
                        {disconnectId}
                      </Typography>
                    </TableCell>
                    {/* Account # */}
                    <TableCell
                      className="sticky-cell-2"
                      sx={{
                        position: 'sticky',
                        left: 198,
                        zIndex: 14,
                        backgroundColor: stickyBg,
                        borderRight: '2px solid',
                        borderRightColor: 'divider',
                        minWidth: 120,
                        width: 120,
                        boxShadow: '2px 0 4px -2px rgba(0, 0, 0, 0.1)',
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: "medium",
                          position: 'relative',
                          zIndex: 1,
                        }}
                      >
                        {disconnect.accountNum || "N/A"}
                      </Typography>
                    </TableCell>
                    {/* Account Holder */}
                    <TableCell>
                      <Typography variant="body2">
                        {fullName(disconnect || {})}
                      </Typography>
                    </TableCell>
                    {/* Account Status */}
                    <TableCell>
                      {disconnect.AccountStatus ? (
                        <Chip
                          label={disconnect.AccountStatus}
                          size="small"
                          {...getChipVariant(
                            disconnect.AccountStatus.toLowerCase() === "active" ? "active" : 
                            disconnect.AccountStatus.toLowerCase() === "inactive" ? "inactive" : 
                            "default",
                            theme
                          )}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    {/* Account Type */}
                    <TableCell>
                      <Typography variant="body2">
                        {disconnect.AccountType || "N/A"}
                      </Typography>
                    </TableCell>
                    {/* Property */}
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {disconnect.propertyName || "N/A"}
                        </Typography>
                        {disconnect.pmName && (
                          <Typography variant="caption" color="text.secondary">
                            {disconnect.pmName}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    {/* Group */}
                    <TableCell>
                      <Chip
                        label={disconnect.propertyGroupName || "N/A"}
                        size="small"
                        sx={{
                          backgroundColor: disconnect.propertyGroupBgColorTag 
                            ? `#${disconnect.propertyGroupBgColorTag}` 
                            : undefined,
                          color: disconnect.propertyGroupBgColorTag ? "#ffffff" : undefined,
                        }}
                      />
                    </TableCell>
                    {/* Address */}
                    <TableCell>
                      <Tooltip title={fullAddressField(disconnect || {})}>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {fullAddressField(disconnect || {})}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    {/* Meter Info */}
                    <TableCell>
                      <Typography variant="body2">
                        {disconnect.meterNum || "N/A"}
                      </Typography>
                    </TableCell>
                    {/* Letter Sent Dates (Type) */}
                    <TableCell>
                      <Tooltip title={formatLetterDates(disconnect)}>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {formatLetterDates(disconnect)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    {/* Disconnect Date */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                        {formatDateSafe(disconnect.disconnectDate, "yyyy-MM-dd")}
                      </Typography>
                    </TableCell>
                    {/* Last Paid */}
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateSafe(disconnect.lastPaymentDate)}
                      </Typography>
                    </TableCell>
                    {/* Disconnect Amt */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                        {formatCurrency(disconnect.disconnectAmt)}
                      </Typography>
                    </TableCell>
                    {/* Account Balance */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                        {formatCurrency(disconnect.accountBalance)}
                      </Typography>
                    </TableCell>
                    {/* Queue Status */}
                    <TableCell>
                      {getStatusChip(
                        normalizedStatus,
                        disconnect.holdReason
                      )}
                      {disconnect.holdNotes && (
                        <Tooltip title={disconnect.holdNotes}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {disconnect.holdNotes.length > 30 
                              ? `${disconnect.holdNotes.substring(0, 30)}...` 
                              : disconnect.holdNotes}
                          </Typography>
                        </Tooltip>
                      )}
                      {normalizedStatus === "Closed" && disconnect.notes && (
                        <Tooltip title={disconnect.notes}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {disconnect.notes.trim().length > 40
                              ? `${disconnect.notes.trim().substring(0, 40)}...`
                              : disconnect.notes.trim()}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    {/* Flags */}
                    <TableCell>
                      {renderFlags(disconnect)}
                    </TableCell>
                    {/* Days in Queue */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {disconnect.daysInQueue || 0} {disconnect.daysInQueue === 1 ? "day" : "days"}
                      </Typography>
                    </TableCell>
                    {/* Created */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDateSafe(disconnect.created)}
                      </Typography>
                    </TableCell>
                    {/* Actions */}
                    <TableCell>
                      <Tooltip title="Actions">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActionMenuAnchor(e.currentTarget);
                            setSelectedDisconnect(disconnect);
                          }}
                          sx={{
                            '&:hover': {
                              cursor: 'pointer',
                              backgroundColor: 'action.hover'
                            }
                          }}
                        >
                          <MoreVertOutlined />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </Box>
        )}
      </TableContainer>
      
      {!isLoading && disconnects.length > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={page - 1}
          onPageChange={handlePageChange}
          rowsPerPage={limit}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
          SelectProps={{
            inputProps: {
              'aria-label': 'rows per page',
            },
            native: true,
          }}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            '& .MuiTablePagination-toolbar': {
              paddingLeft: 2,
              paddingRight: 2,
              display: 'flex',
              alignItems: 'center',
              visibility: 'visible !important',
            },
            '& .MuiTablePagination-actions': {
              display: 'flex !important',
              visibility: 'visible !important',
              marginLeft: 2,
              width: 'auto !important',
              minWidth: '160px !important',
              '& .MuiIconButton-root': {
                width: '40px !important',
                height: '40px !important',
                display: 'inline-flex !important',
                visibility: 'visible !important',
                opacity: '1 !important',
                color: 'text.secondary',
                backgroundColor: 'background.paper',
                '&:hover:not(.Mui-disabled)': {
                  backgroundColor: 'action.hover !important',
                  borderColor: 'text.secondary',
                  color: 'text.primary',
                },
                '&:active:not(.Mui-disabled)': {
                  backgroundColor: 'action.selected !important',
                  transform: 'translateY(1px)',
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 2,
                },
                '&.Mui-disabled': {
                  color: 'text.disabled',
                  opacity: 1,
                  cursor: 'not-allowed',
                },
              },
            },
            '& .MuiTablePagination-selectLabel': { marginBottom: 0, visibility: 'visible !important' },
            '& .MuiTablePagination-displayedRows': { marginBottom: 0, visibility: 'visible !important' },
            '& .MuiTextField-root .MuiOutlinedInput-root:hover': {
              backgroundColor: 'action.selected !important',
            },
          }}
        />
      )}

      <Dialog
        open={holdModalOpen}
        onClose={() => setHoldModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Hold Disconnection</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Please provide a reason for holding this disconnection.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Hold Reason"
            value={holdNote}
            onChange={(e) => setHoldNote(e.target.value)}
            placeholder="Enter reason for holding this disconnection..."
            sx={{ mt: 2 }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHoldModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleHoldSubmit}
            variant="contained"
            color="primary"
            disabled={!holdNote.trim()}
          >
            Hold
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={revertModalOpen}
        onClose={() => setRevertModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Revert Disconnection</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Are you sure you want to revert this disconnection to the previous stage?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevertModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRevertSubmit}
            variant="contained"
            color="primary"
          >
            Revert
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rescheduleModalOpen}
        onClose={() => setRescheduleModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reschedule Disconnection</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Are you sure you want to reschedule this disconnect? This will update the manual hold date to the current date.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRescheduleModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRescheduleSubmit}
            variant="contained"
            color="primary"
          >
            Reschedule
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={revertMultiDialogOpen}
        onClose={() => setRevertMultiDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Revert Selected Disconnects</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="primary" gutterBottom>
            This will revert {selectedDisconnects.size} selected disconnect{selectedDisconnects.size !== 1 ? 's' : ''} to their previous stage
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevertMultiDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              try {
                const selectedArray = Array.from(selectedDisconnects);
                
                // Create maps of UUID to name and ID for display purposes
                const uuidToNameMap = new Map();
                const uuidToIdMap = new Map();
                selectedArray.forEach((uuid) => {
                  const disconnect = disconnects.find((d) => (d.uuid || d.disconnectUuid) === uuid);
                  if (disconnect) {
                    const name = fullName(disconnect);
                    const disconnectId = disconnect.id || disconnect.disconnectId || 'N/A';
                    uuidToNameMap.set(uuid, name);
                    uuidToIdMap.set(uuid, disconnectId);
                  }
                });
                setDisconnectUuidToName(uuidToNameMap);
                setDisconnectUuidToId(uuidToIdMap);
                
                const response = await props.revertAccountDisconnectsBulk(selectedArray);
                
                // Always show results modal
                setBulkRevertResults(response);
                setRevertMultiDialogOpen(false);
                setSelectedDisconnects(new Set());
                setBulkRevertDetailsOpen(true);
                
                // Refresh the queue data
                const queryParams = buildQueryParams();
                props.getAccountDisconnectsForQueue(queryParams, true);
              } catch (error) {
                console.error("Error reverting disconnects:", error);
                setRevertMultiDialogOpen(false);
              }
            }}
            variant="contained"
            sx={{ bgcolor: "red"}}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Revert Details Modal */}
      <Dialog
        open={bulkRevertDetailsOpen}
        onClose={() => setBulkRevertDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Bulk Revert Results</DialogTitle>
        <DialogContent>
          {bulkRevertResults && (() => {
            const results = bulkRevertResults.results || [];
            const errors = bulkRevertResults.errors || [];
            const successCount = results.length;
            const errorCount = errors.length;
            const totalCount = successCount + errorCount;

            return (
              <Box sx={{ mt: 1 }}>
                {/* Total Counts Summary */}
                <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="h6" gutterBottom>
                    Summary
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Total Processed
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {totalCount}
                      </Typography>
                    </Box>
                    {successCount > 0 && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Successful
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                          {successCount}
                        </Typography>
                      </Box>
                    )}
                    {errorCount > 0 && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Unsuccessful
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                          {errorCount}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Successful Results Box */}
                {successCount > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: 'success.light', 
                      borderRadius: 1, 
                      border: '2px solid', 
                      borderColor: 'success.main',
                      mb: 2
                    }}>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                        ✓ Successful Reverts ({successCount})
                      </Typography>
                    </Box>
                    <TableContainer component={Paper} sx={{ maxHeight: 300, border: '1px solid', borderColor: 'success.main' }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: 'success.light' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Account Holder</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Disconnect ID</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Previous Stage</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>New Stage</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Account ID</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {results.map((result, index) => {
                            const name = disconnectUuidToName.get(result.disconnectUuid) || 'N/A';
                            return (
                              <TableRow key={index}>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {name}
                                  </Typography>
                                </TableCell>
                                <TableCell>{result.disconnectId}</TableCell>
                                <TableCell>{result.previousStage}</TableCell>
                                <TableCell>{result.newStage}</TableCell>
                                <TableCell>{result.accountId}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Unsuccessful Results Box */}
                {errorCount > 0 && (
                  <Box>
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: 'error.light', 
                      borderRadius: 1, 
                      border: '2px solid', 
                      borderColor: 'error.main',
                      mb: 2
                    }}>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                        ✗ Unsuccessful Reverts ({errorCount})
                      </Typography>
                    </Box>
                    <TableContainer component={Paper} sx={{ maxHeight: 300, border: '1px solid', borderColor: 'error.main' }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: 'error.light' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Account Holder</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Disconnect ID</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Error</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {errors.map((error, index) => {
                            const name = disconnectUuidToName.get(error.disconnectUuid) || 'N/A';
                            const disconnectId = disconnectUuidToId.get(error.disconnectUuid) || 'N/A';
                            return (
                              <TableRow key={index}>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {name}
                                  </Typography>
                                </TableCell>
                                <TableCell>{disconnectId}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="error">
                                    {error.error}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkRevertDetailsOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Hold Dialog */}
      <Dialog
        open={bulkHoldDialogOpen}
        onClose={() => setBulkHoldDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Hold Selected Disconnects</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Please provide a reason for holding {selectedDisconnects.size} selected disconnection{selectedDisconnects.size !== 1 ? 's' : ''}.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Hold Reason"
            value={holdNote}
            onChange={(e) => setHoldNote(e.target.value)}
            placeholder="Enter reason for holding these disconnections..."
            sx={{ mt: 2 }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkHoldDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              if (!holdNote.trim()) {
                return;
              }
              try {
                const selectedArray = Array.from(selectedDisconnects);
                
                // Create maps of UUID to name and ID for display purposes
                const uuidToNameMap = new Map();
                const uuidToIdMap = new Map();
                const disconnectHolds = [];
                
                selectedArray.forEach((uuid) => {
                  const disconnect = disconnects.find((d) => (d.uuid || d.disconnectUuid) === uuid);
                  if (disconnect) {
                    const name = fullName(disconnect);
                    const disconnectId = disconnect.id || disconnect.disconnectId || 'N/A';
                    const accountId = disconnect.accountId || disconnect.id;
                    
                    uuidToNameMap.set(uuid, name);
                    uuidToIdMap.set(uuid, disconnectId);
                    
                    if (accountId) {
                      const expiresAtDate = startOfDay(addDays(new Date(), 30));
                      const expiresAt = format(expiresAtDate, "yyyy-MM-dd HH:mm:ss");
                      
                      disconnectHolds.push({
                        holdType: 'ACCOUNT',
                        accountId: accountId,
                        disconnectUuid: uuid,
                        reason: holdNote.trim(),
                        notes: holdNote.trim(),
                        expiresAt: expiresAt,
                        isActive: 1,
                        liftReason: '',
                      });
                    }
                  }
                });
                
                setDisconnectUuidToName(uuidToNameMap);
                setDisconnectUuidToId(uuidToIdMap);
                
                const response = await props.createDisconnectHoldsBulk(disconnectHolds);
                
                // Show results modal
                setBulkActionResults(response);
                setBulkActionType('hold');
                setBulkHoldDialogOpen(false);
                setHoldNote("");
                setSelectedDisconnects(new Set());
                setBulkActionResultsOpen(true);
                
                // Refresh the queue data
                const queryParams = buildQueryParams();
                props.getAccountDisconnectsForQueue(queryParams, true);
              } catch (error) {
                console.error("Error holding disconnects:", error);
                dispatch(setSnackbar(true, "error", error.message || "Failed to hold disconnections"));
                setBulkHoldDialogOpen(false);
              }
            }}
            variant="contained"
            color="primary"
            disabled={!holdNote.trim()}
          >
            Hold
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Run Now Dialog */}
      <Dialog
        open={bulkRunNowDialogOpen}
        onClose={() => setBulkRunNowDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Run Now Selected Disconnects</DialogTitle>
        <DialogContent>
          {(() => {
            const selectedArray = Array.from(selectedDisconnects);
            let finalCount = 0;
            let disconnectCount = 0;
            selectedArray.forEach((uuid) => {
              const disc = disconnects.find((d) => (d.uuid || d.disconnectUuid) === uuid);
              if (disc) {
                const stage = getCurrentStage(disc);
                if (stage === "14-day" || stage === "10-day") {
                  finalCount++;
                } else {
                  disconnectCount++;
                }
              }
            });
            return (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Are you sure you want to process {selectedDisconnects.size} selected disconnect{selectedDisconnects.size !== 1 ? 's' : ''}?
                </Typography>
                {finalCount > 0 && (
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: 'warning.main' }}>
                    {finalCount} account{finalCount !== 1 ? 's' : ''} on 14/10-day notice → will generate Final Notice
                  </Typography>
                )}
                {disconnectCount > 0 && (
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: 'error.main' }}>
                    {disconnectCount} account{disconnectCount !== 1 ? 's' : ''} on Final Notice → will process Disconnect
                  </Typography>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkRunNowDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              try {
                const selectedArray = Array.from(selectedDisconnects);
                
                const uuidToNameMap = new Map();
                const uuidToIdMap = new Map();
                const finalReadyUuids = [];
                const disconnectUuids = [];

                selectedArray.forEach((uuid) => {
                  const disc = disconnects.find((d) => (d.uuid || d.disconnectUuid) === uuid);
                  if (disc) {
                    const name = fullName(disc);
                    const disconnectId = disc.id || disc.disconnectId || 'N/A';
                    uuidToNameMap.set(uuid, name);
                    uuidToIdMap.set(uuid, disconnectId);

                    const stage = getCurrentStage(disc);
                    if (stage === "14-day" || stage === "10-day") {
                      finalReadyUuids.push(uuid);
                    } else {
                      disconnectUuids.push(uuid);
                    }
                  }
                });
                
                setDisconnectUuidToName(uuidToNameMap);
                setDisconnectUuidToId(uuidToIdMap);
                
                const promises = [];
                if (finalReadyUuids.length > 0) {
                  promises.push(props.markDisconnectsAsPendingBulk(finalReadyUuids, false, 'final_ready'));
                }
                if (disconnectUuids.length > 0) {
                  promises.push(props.markDisconnectsAsPendingBulk(disconnectUuids, false, ''));
                }
                
                await Promise.all(promises);
                
                setBulkActionType('runNow');
                setBulkRunNowDialogOpen(false);
                setSelectedDisconnects(new Set());
                
                dispatch(setSnackbar(true, "success", `Run Now processed: ${finalReadyUuids.length} Final Notice(s), ${disconnectUuids.length} Disconnect(s)`));
                
                const queryParams = buildQueryParams();
                props.getAccountDisconnectsForQueue(queryParams, true);
              } catch (error) {
                console.error("Error marking disconnects as pending:", error);
                dispatch(setSnackbar(true, "error", error.message || "Failed to mark disconnects as pending"));
                setBulkRunNowDialogOpen(false);
              }
            }}
            variant="contained"
            color="success"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Reschedule Dialog */}
      <Dialog
        open={bulkRescheduleDialogOpen}
        onClose={() => setBulkRescheduleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reschedule Selected Disconnects</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Are you sure you want to reschedule {selectedDisconnects.size} selected disconnect{selectedDisconnects.size !== 1 ? 's' : ''}? This will update the manual hold date to the current date.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkRescheduleDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              try {
                const selectedArray = Array.from(selectedDisconnects);
                
                // Create maps of UUID to name and ID for display purposes
                const uuidToNameMap = new Map();
                const uuidToIdMap = new Map();
                selectedArray.forEach((uuid) => {
                  const disconnect = disconnects.find((d) => (d.uuid || d.disconnectUuid) === uuid);
                  if (disconnect) {
                    const name = fullName(disconnect);
                    const disconnectId = disconnect.id || disconnect.disconnectId || 'N/A';
                    uuidToNameMap.set(uuid, name);
                    uuidToIdMap.set(uuid, disconnectId);
                  }
                });
                
                setDisconnectUuidToName(uuidToNameMap);
                setDisconnectUuidToId(uuidToIdMap);
                
                const response = await props.rescheduleAccountDisconnectsBulk(selectedArray);
                
                // Show results modal
                setBulkActionResults(response);
                setBulkActionType('reschedule');
                setBulkRescheduleDialogOpen(false);
                setSelectedDisconnects(new Set());
                setBulkActionResultsOpen(true);
                
                // Refresh the queue data
                const queryParams = buildQueryParams();
                props.getAccountDisconnectsForQueue(queryParams, true);
              } catch (error) {
                console.error("Error rescheduling disconnects:", error);
                dispatch(setSnackbar(true, "error", error.message || "Failed to reschedule disconnections"));
                setBulkRescheduleDialogOpen(false);
              }
            }}
            variant="contained"
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Resend Notice Dialog */}
      <Dialog
        open={bulkResendNoticeDialogOpen}
        onClose={() => setBulkResendNoticeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Resend Notice Selected Disconnects</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Are you sure you want to resend notices for {selectedDisconnects.size} selected disconnect{selectedDisconnects.size !== 1 ? 's' : ''}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkResendNoticeDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              try {
                const selectedArray = Array.from(selectedDisconnects);
                
                // Create maps of UUID to name and ID for display purposes
                const uuidToNameMap = new Map();
                const uuidToIdMap = new Map();
                selectedArray.forEach((uuid) => {
                  const disconnect = disconnects.find((d) => (d.uuid || d.disconnectUuid) === uuid);
                  if (disconnect) {
                    const name = fullName(disconnect);
                    const disconnectId = disconnect.id || disconnect.disconnectId || 'N/A';
                    uuidToNameMap.set(uuid, name);
                    uuidToIdMap.set(uuid, disconnectId);
                  }
                });
                
                setDisconnectUuidToName(uuidToNameMap);
                setDisconnectUuidToId(uuidToIdMap);
                
                const response = await props.resendDisconnectNoticesBulk(selectedArray);
                
                // Show results modal
                setBulkActionResults(response);
                setBulkActionType('resendNotice');
                setBulkResendNoticeDialogOpen(false);
                setSelectedDisconnects(new Set());
                setBulkActionResultsOpen(true);
                
                // Refresh the queue data
                const queryParams = buildQueryParams();
                props.getAccountDisconnectsForQueue(queryParams, true);
              } catch (error) {
                console.error("Error resending notices:", error);
                dispatch(setSnackbar(true, "error", error.message || "Failed to resend notices"));
                setBulkResendNoticeDialogOpen(false);
              }
            }}
            variant="contained"
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Action Results Modal */}
      <Dialog
        open={bulkActionResultsOpen}
        onClose={() => setBulkActionResultsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Bulk {bulkActionType === 'hold' ? 'Hold' : bulkActionType === 'runNow' ? 'Run Now' : bulkActionType === 'reschedule' ? 'Reschedule' : bulkActionType === 'resendNotice' ? 'Resend Notice' : 'Action'} Results
        </DialogTitle>
        <DialogContent>
          {bulkActionResults && (() => {
            const results = bulkActionResults.results || [];
            const errors = bulkActionResults.errors || [];
            const successCount = results.length;
            const errorCount = errors.length;
            const totalCount = successCount + errorCount;

            return (
              <Box sx={{ mt: 1 }}>
                {/* Total Counts Summary */}
                <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="h6" gutterBottom>
                    Summary
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Total Processed
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {totalCount}
                      </Typography>
                    </Box>
                    {successCount > 0 && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Successful
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                          {successCount}
                        </Typography>
                      </Box>
                    )}
                    {errorCount > 0 && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Unsuccessful
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                          {errorCount}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Successful Results Box */}
                {successCount > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: 'success.light', 
                      borderRadius: 1, 
                      border: '2px solid', 
                      borderColor: 'success.main',
                      mb: 2
                    }}>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                        ✓ Successful ({successCount})
                      </Typography>
                    </Box>
                    <TableContainer component={Paper} sx={{ maxHeight: 300, border: '1px solid', borderColor: 'success.main' }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: 'success.light' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Account Holder</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Disconnect ID</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {results.map((result, index) => {
                            const uuid = result.disconnectUuid || result.uuid;
                            const name = disconnectUuidToName.get(uuid) || 'N/A';
                            const disconnectId = result.disconnectId || disconnectUuidToId.get(uuid) || 'N/A';
                            return (
                              <TableRow key={index}>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {name}
                                  </Typography>
                                </TableCell>
                                <TableCell>{disconnectId}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Unsuccessful Results Box */}
                {errorCount > 0 && (
                  <Box>
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: 'error.light', 
                      borderRadius: 1, 
                      border: '2px solid', 
                      borderColor: 'error.main',
                      mb: 2
                    }}>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                        ✗ Unsuccessful ({errorCount})
                      </Typography>
                    </Box>
                    <TableContainer component={Paper} sx={{ maxHeight: 300, border: '1px solid', borderColor: 'error.main' }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: 'error.light' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Account Holder</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Disconnect ID</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Error</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {errors.map((error, index) => {
                            const uuid = error.disconnectUuid || error.uuid;
                            const name = disconnectUuidToName.get(uuid) || 'N/A';
                            const disconnectId = error.disconnectId || disconnectUuidToId.get(uuid) || 'N/A';
                            return (
                              <TableRow key={index}>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {name}
                                  </Typography>
                                </TableCell>
                                <TableCell>{disconnectId}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="error">
                                    {error.error || error.message || 'Unknown error'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkActionResultsOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>


      <Popover
        open={Boolean(actionMenuAnchor)}
        anchorEl={actionMenuAnchor}
        onClose={() => {
          setActionMenuAnchor(null);
          setSelectedDisconnect(null);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            p: 2,
            minWidth: '300px',
          }
        }}
      >
        <Box sx={{ display: "flex", gap: 1, justifyContent: "center", alignItems: "center" }}>
          <Tooltip title={selectedDisconnect ? getRunNowLabel(selectedDisconnect) : "Run Now"}>
            <IconButton
              onClick={() => {
                if (selectedDisconnect) {
                  handleRunNow(selectedDisconnect);
                  setActionMenuAnchor(null);
                }
              }}
              size="small"
              sx={{
                '&:hover': {
                  cursor: 'pointer',
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <PlayArrowOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Hold">
            <IconButton
              onClick={() => {
                if (selectedDisconnect) {
                  handleHold(selectedDisconnect);
                  setActionMenuAnchor(null);
                }
              }}
              size="small"
              sx={{
                '&:hover': {
                  cursor: 'pointer',
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <PauseOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reschedule">
            <IconButton
              onClick={() => {
                if (selectedDisconnect) {
                  handleReschedule(selectedDisconnect);
                  setActionMenuAnchor(null);
                }
              }}
              size="small"
              sx={{
                '&:hover': {
                  cursor: 'pointer',
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <CalendarTodayOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Resend Letter">
            <IconButton
              onClick={() => {
                if (selectedDisconnect) {
                  handleResendLetter(selectedDisconnect);
                  setActionMenuAnchor(null);
                }
              }}
              size="small"
              sx={{
                '&:hover': {
                  cursor: 'pointer',
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <EmailOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Revert">
            <IconButton
              onClick={() => {
                if (selectedDisconnect) {
                  handleRevert(selectedDisconnect);
                  setActionMenuAnchor(null);
                }
              }}
              size="small"
              sx={{
                '&:hover': {
                  cursor: 'pointer',
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <ReplayOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close Disconnect">
            <IconButton
              onClick={() => {
                if (selectedDisconnect) {
                  handleCloseDisconnect(selectedDisconnect);
                }
              }}
              size="small"
              sx={{
                '&:hover': {
                  cursor: 'pointer',
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <ClearOutlined />
            </IconButton>
          </Tooltip>
        </Box>
      </Popover>

      <Dialog
        open={closeModalOpen}
        onClose={() => setCloseModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Close Disconnect</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Are you sure you want to close this disconnection? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseModalOpen(false)}>Cancel</Button>
          <Button onClick={handleCloseSubmit} variant="contained" color="primary">
            Close Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

DisconnectQueue.propTypes = {
  accountDisconnects: PropTypes.object.isRequired,
  properties: PropTypes.object.isRequired,
  getAccountDisconnectsForQueue: PropTypes.func.isRequired,
  getProperties: PropTypes.func.isRequired,
  getPropertyGroups: PropTypes.func.isRequired,
  revertAccountDisconnect: PropTypes.func.isRequired,
  revertAccountDisconnectsBulk: PropTypes.func.isRequired,
  rescheduleAccountDisconnect: PropTypes.func.isRequired,
  createDisconnectHold: PropTypes.func.isRequired,
  createDisconnectHoldsBulk: PropTypes.func.isRequired,
  markDisconnectAsPending: PropTypes.func.isRequired,
  markDisconnectsAsPendingBulk: PropTypes.func.isRequired,
  resendDisconnectNotice: PropTypes.func.isRequired,
  resendDisconnectNoticesBulk: PropTypes.func.isRequired,
  rescheduleAccountDisconnectsBulk: PropTypes.func.isRequired,
  deleteByUuid: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
};

function DisconnectQueueState(state) {
  return {
    accountDisconnects: state.accountDisconnects,
    properties: state.properties,
    user: state.user,
  };
}

export default connect(DisconnectQueueState, {
  getAccountDisconnectsForQueue: getAccountDisconnectsForQueue,
  getProperties: getProperties,
  getPropertyGroups: getPropertyGroups,
  revertAccountDisconnect: revertAccountDisconnect,
  revertAccountDisconnectsBulk: revertAccountDisconnectsBulk,
  rescheduleAccountDisconnect: rescheduleAccountDisconnect,
  rescheduleAccountDisconnectsBulk: rescheduleAccountDisconnectsBulk,
  createDisconnectHold: createDisconnectHold,
  createDisconnectHoldsBulk: createDisconnectHoldsBulk,
  markDisconnectAsPending: markDisconnectAsPending,
  markDisconnectsAsPendingBulk: markDisconnectsAsPendingBulk,
  resendDisconnectNotice: resendDisconnectNotice,
  resendDisconnectNoticesBulk: resendDisconnectNoticesBulk,
  deleteByUuid: deleteByUuid,
})(DisconnectQueue);

